import { useQuery } from "@tanstack/react-query";
import OCGatewayClient from "../client/OCGatewayClient";
import { DeviceRebootLog } from "../models/DeviceRebootLog";
import { gatewayUserScopedQueryOptions } from "./gatewayQueryUtils";

interface DeviceRebootLogsOptions {
    hours?: number;
    limit?: number;
    startTime?: string | Date;
    endTime?: string | Date;
}

/**
 * One entry per device reboot.
 *
 * Replaces `useDeviceErrorLogsQuery`, which returned the row-per-field shape
 * Timestream's storage layout imposed on callers. Reboot logs now live in
 * Aurora and the gateway serves them as objects.
 *
 * Instances are constructed so `timestamp` is a Date rather than the ISO string
 * that comes over the wire — same pattern as `pushNotificationConfigQuery`.
 */
export function useDeviceRebootLogsQuery(deviceId: string, options?: DeviceRebootLogsOptions) {
    const query = useQuery({
        queryKey: ['getDeviceRebootLogs', deviceId, options],
        enabled: !!deviceId,
        queryFn: async () => {
            const requestParams = {
                deviceId,
                hours: options?.hours ?? 24,
                limit: options?.limit ?? 200,
                ...(options?.startTime ? { startTime: options.startTime } : {}),
                ...(options?.endTime ? { endTime: options.endTime } : {})
            };

            const response = await OCGatewayClient.request('getDeviceRebootLogs', requestParams);

            return (response as Record<string, any>[]).map((entry) => new DeviceRebootLog(entry));
        },
        ...gatewayUserScopedQueryOptions,
        staleTime: 30 * 1000,
        gcTime: 1000 * 60 * 60,
    });

    return query;
}
